/**
 * Root-level back navigation from /results: browser Back must land on /dashboard.
 * App Router has no beforePopState; a capture-phase popstate listener survives /results unmount.
 */

import { INTERVIEW_RESULTS_PATH } from '@/lib/config'

export const SESSION_KEY_INTERVIEW_RESULTS_PAGE_ACTIVE = 'interviewResultsPageActive'

const DASHBOARD_PATH = '/dashboard'

/** Strip trailing slashes except root — matches Next default and trailingSlash: true. */
export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/'
  return pathname.replace(/\/+$/, '') || '/'
}

/** True if URL is the interview results page (with or without trailing slash). */
export function isInterviewResultsPath(pathname: string): boolean {
  return normalizePathname(pathname) === normalizePathname(INTERVIEW_RESULTS_PATH)
}

let listenerInstalled = false

export function setInterviewResultsPageActive(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(SESSION_KEY_INTERVIEW_RESULTS_PAGE_ACTIVE, '1')
  } catch {
    // ignore
  }
}

export function clearInterviewResultsPageActive(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(SESSION_KEY_INTERVIEW_RESULTS_PAGE_ACTIVE)
  } catch {
    // ignore
  }
}

function isResultsActive(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY_INTERVIEW_RESULTS_PAGE_ACTIVE) === '1'
  } catch {
    return false
  }
}

/**
 * Idempotent: register once. Call from a client component mounted in root layout.
 */
export function installResultsBackNavigationGuard(): void {
  if (typeof window === 'undefined' || listenerInstalled) return
  listenerInstalled = true

  window.addEventListener(
    'popstate',
    () => {
      if (!isResultsActive()) return

      const path = window.location.pathname
      // Same path (incl. hash-only history tweaks) or trailing-slash variant — do not redirect
      if (isInterviewResultsPath(path)) return

      clearInterviewResultsPageActive()
      window.location.replace(DASHBOARD_PATH)
    },
    true
  )
}
